import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getApplicationsStack } from 'features/applications/actions';
import { getApplicationsDetailStack } from 'features/applications/detail/actions';
import { setTopNavColorDark } from 'features/navigation/header/actions';
import styled, { css } from 'styled-components';
import { palette } from 'theme';
import { Link as ScrollLink, Element as ScrollElement } from 'react-scroll';
import loadable from '@loadable/component';

import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import Paper from '@material-ui/core/Paper';

import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';
import Container from 'components/Container';
import MetaTags from 'components/MetaTags';
import CircularLoader from 'components/CircularLoader';
import SwiftypeTags from 'components/SwiftypeTags';
import PaddedDivider from 'components/PaddedDivider';
import { PageHeaderGrid, PageHeaderText } from 'components/PageHeader';
import { SectionWrapper } from 'components/Section';
import { ResponsiveBackgroundImg } from 'components/ResponsiveImg';
import ApplicationsNav from '../elements/ApplicationsNav';
import ApplicationQA from '../elements/ApplicationQA';
import { ApplicationSection } from './elements/ApplicationSection';

const StickyNav = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.StickyNav
});
const StickyListItem = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.StickyListItem
});
const ListItemNumber = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.ListItemNumber
});
const StickyListItemText = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.StickyListItemText
});
const ApplicationRelatedSection = loadable(
  () => import('./elements/ApplicationSection'),
  {
    resolveComponent: components => components.ApplicationRelatedSection
  }
);
const ApplicationContactSection = loadable(
  () => import('./elements/ApplicationSection'),
  {
    resolveComponent: components => components.ApplicationContactSection
  }
);
const ProductCard = loadable(() =>
  import('views/Product/Family/elements/ProductCard')
);

const NavWrapper = styled.div`
  background: ${palette.grey[50]};
  ${props =>
    props.top
      ? css`
          margin-top: -64px;
          padding-top: 64px;
        `
      : css`
          padding-top: 15px;
        `}
  padding-bottom: 15px;
  width: 100%;
  position: relative;
  z-index: 2;
`;

const ApplicationPageHeaderGrid = styled(PageHeaderGrid)`
  min-height: 20vh;
  @media (max-height: 900px) {
    min-height: 60vh;
  }
`;

const ApplicationWrapper = styled.div`
  position: relative;
  min-height: 80vh;
`;

const ApplicationContent = styled(Container)`
  position: relative;
  z-index: 1;
  padding-bottom: 30px;
  min-height: 100vh;
`;

const ApplicationBackgroundImg = styled(ResponsiveBackgroundImg)`
  z-index: 0;
  top: -70px;
  opacity: 0.25;
  max-height: 80vh;
`;

const CTAs = styled.div`
  padding: 40px;
`;

const AppsStickyListItem = ({ name, number, ...other }) => (
  <StickyListItem
    button
    component={ScrollLink}
    activeClass="active"
    to={name}
    spy={true}
    smooth={true}
    duration={300}
    offset={25}
    {...other}
  >
    <ListItemNumber variant="overline" component="span">
      {String(number).padStart(2, 0)}.
    </ListItemNumber>
    <StickyListItemText primary={name} />
  </StickyListItem>
);

class ApplicationsDetail extends React.PureComponent {
  componentWillMount() {
    this.props.setTopNavColorDark();
  }

  componentDidMount() {
    this.props.getApplicationsStack();
    this.props.getApplicationsDetailStack(this.props.match.url);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.match.url !== this.props.match.url) {
      this.props.getApplicationsDetailStack(this.props.match.url);
    }
  }

  render() {
    const { landingStack, detailStack, loading } = this.props;

    return (
      <Fragment>
        {landingStack.list && landingStack.list.applications.length > 0 && (
          <NavWrapper top>
            <Container>
              <ApplicationsNav
                applicationItems={landingStack.list.applications}
                activeID={detailStack && landingStack ? detailStack.uid : false}
                backgroundColor={palette.grey[50]}
                showBack
              />
            </Container>
          </NavWrapper>
        )}
        {!loading ? (
          <>
            <MetaTags stack={detailStack} />
            <SwiftypeTags
              stack={detailStack}
              title={detailStack.title}
              sections={detailStack.sections.map(section => section.name)}
              contentType="Application"
              image={detailStack.icon && detailStack.icon.url}
            />
            <ApplicationWrapper
              data-swiftype-name="body"
              data-swiftype-type="text"
            >
              {detailStack.background_image && (
                <ApplicationBackgroundImg
                  src={detailStack.background_image.url}
                  defaultWidth={800}
                  backgroundcolor="white"
                />
              )}
              <ApplicationContent>
                <ApplicationPageHeaderGrid container>
                  <Grid item xs={12} md={10}>
                    <PageHeaderText heading={detailStack.title} />
                    <ApplicationQA
                      question={detailStack.q_and_a.question}
                      answer={detailStack.q_and_a.answer}
                    />
                  </Grid>
                </ApplicationPageHeaderGrid>
                <PaddedDivider />
                <Grid container spacing={32}>
                  <Grid item xs={12} md={8}>
                    {detailStack.sections.map((section, idx) => (
                      <ScrollElement key={idx} name={section.name}>
                        <PrerenderSafeLazyLoad>
                          <ApplicationSection section={section} />
                          <PaddedDivider spacing={1} />
                        </PrerenderSafeLazyLoad>
                      </ScrollElement>
                    ))}

                    {detailStack.related_content.length > 0 && (
                      <ScrollElement name={detailStack.related_content_label}>
                        <PrerenderSafeLazyLoad>
                          <ApplicationRelatedSection
                            section={detailStack.related_content}
                            label={detailStack.related_content_label}
                          />
                        </PrerenderSafeLazyLoad>
                      </ScrollElement>
                    )}

                    {detailStack.contact && detailStack.contact.show && (
                      <ScrollElement name={detailStack.contact.name}>
                        <PrerenderSafeLazyLoad>
                          <ApplicationContactSection
                            section={detailStack.contact}
                          />
                        </PrerenderSafeLazyLoad>
                      </ScrollElement>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {detailStack.show_section_menu && (
                      <PrerenderSafeLazyLoad>
                        <StickyNav>
                          <Paper>
                            <List component="nav">
                              <ListSubheader disableSticky>
                                Contents
                              </ListSubheader>
                              {detailStack.sections.map((section, idx) => (
                                <AppsStickyListItem
                                  name={section.name}
                                  number={idx + 1}
                                  key={idx}
                                />
                              ))}
                              {detailStack.related_content.length > 0 && (
                                <AppsStickyListItem
                                  name={detailStack.related_content_label}
                                  number={detailStack.sections.length + 1}
                                />
                              )}
                              {detailStack.contact &&
                                detailStack.contact.show && (
                                  <AppsStickyListItem
                                    name={detailStack.contact.name}
                                    number={
                                      detailStack.sections.length +
                                      (detailStack.related_content.length > 0
                                        ? 2
                                        : 1)
                                    }
                                  />
                                )}
                            </List>
                          </Paper>
                        </StickyNav>
                      </PrerenderSafeLazyLoad>
                    )}
                  </Grid>
                </Grid>
              </ApplicationContent>
              <PrerenderSafeLazyLoad>
                <SectionWrapper>
                  {detailStack.ctas && detailStack.ctas.length > 0 && (
                    <>
                      <CTAs>
                        <Grid container spacing={40}>
                          {detailStack.ctas.map((block, idx) =>
                            block?.enhanced_product?.product_reference?.[0] ||
                            block?.product?.product_reference?.[0] ? (
                              <Grid item xs={12} md={4} lg key={idx}>
                                {block?.product?.product_reference?.[0] && (
                                  <ProductCard
                                    backgroundurl={
                                      block.product.product_reference[0]
                                        .overview.section_image.url
                                    }
                                    iconurl={
                                      block.product.product_reference[0]
                                        .product_header_cta.icon.url
                                    }
                                    producturl={
                                      block.product.product_reference[0].url
                                    }
                                    title={
                                      block.product.product_reference[0].header
                                        .title
                                    }
                                    subtitle={
                                      block.product.product_reference[0].header
                                        .subtitle
                                    }
                                    cta="Learn More"
                                  />
                                )}
                                {block?.enhanced_product
                                  ?.product_reference?.[0] && (
                                  <ProductCard
                                    backgroundurl={
                                      block.enhanced_product
                                        .product_reference[0].hero
                                        .background_image.url
                                    }
                                    iconurl={
                                      block.enhanced_product
                                        .product_reference[0].hero.icon.url
                                    }
                                    producturl={
                                      block.enhanced_product
                                        .product_reference[0].url
                                    }
                                    title={
                                      block.enhanced_product
                                        .product_reference[0].hero.heading
                                    }
                                    subtitle={
                                      block.enhanced_product.subheading ||
                                      block.enhanced_product
                                        .product_reference[0].hero.subheading
                                    }
                                    cta="Learn More"
                                  />
                                )}
                              </Grid>
                            ) : null
                          )}
                        </Grid>
                      </CTAs>
                      <Divider />
                    </>
                  )}
                </SectionWrapper>
              </PrerenderSafeLazyLoad>
            </ApplicationWrapper>
            <PrerenderSafeLazyLoad>
              <NavWrapper>
                <Container>
                  {landingStack.list && (
                    <ApplicationsNav
                      applicationItems={landingStack.list.applications}
                      activeID={
                        detailStack && landingStack ? detailStack.uid : false
                      }
                      backgroundColor={palette.grey[50]}
                      showBack
                    />
                  )}
                </Container>
              </NavWrapper>
            </PrerenderSafeLazyLoad>
          </>
        ) : (
          <CircularLoader />
        )}
      </Fragment>
    );
  }
}

ApplicationsDetail.propTypes = {
  detailStack: PropTypes.object,
  landingStack: PropTypes.object
};

export default connect(
  state => ({
    detailStack: state.applicationsDetail.stack,
    landingStack: state.applicationsLanding.stack,
    loading: state.applicationsDetail.loading
  }),
  {
    getApplicationsDetailStack,
    getApplicationsStack,
    setTopNavColorDark
  }
)(ApplicationsDetail);
